/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef } from "react";
import interact from "interactjs";
import {
  tap,
  editObject,
  interactionStart,
  mouseEnter,
  mouseLeave,
} from "../reducers/slices/phases.js";
import PropTypes from "prop-types";
import Helper from "../functions/Helper.js";
import { useTheme } from "@mui/material/styles";
import { ArrowMarker } from "../markers";
import DragCircle from "./DragCircle.js";
import useCurrentValue from "../../hooks/useCurrentValue.js";

const updatePosition = () => {};

const getStrokeColor = (status, defaultColor, theme) => {
  if (status === "SELECTED_MULTIPLE" || status === "HOVERING_ANIMATOR") {
    return theme.palette.active;
  } else {
    return defaultColor;
  }
};

export default function Handoff(props) {
  const theme = useTheme();
  const dispatch = props.dispatch;
  const propsRef = useRef(props);

  let { arrowStrokeWidth } = props.objectSizes;

  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  function dragMoveListener(event, pointType) {
    let target = event.target;

    let handlePoints = ["s", "m", "e"];
    let domPoints = {
      s: {
        visible: document.getElementById(props.uId + "_s"),
        invisible: document.getElementById(props.uId + "_si"),
      },
      m: {
        visible: document.getElementById(props.uId + "_m"),
        invisible: document.getElementById(props.uId + "_mi"),
      },
      e: {
        visible: document.getElementById(props.uId + "_e"),
        invisible: document.getElementById(props.uId + "_ei"),
      },
    };

    let deltaX =
      (parseFloat(target.getAttribute("data-x")) || 0) +
      event.dx / propsRef.current.pixelPerFoot;
    let deltaY =
      (parseFloat(target.getAttribute("data-y")) || 0) +
      event.dy / propsRef.current.pixelPerFoot;
    let shape = propsRef.current.shape;
    target.setAttribute("data-x", deltaX);
    target.setAttribute("data-y", deltaY);

    let svg = document.querySelector("#editor_court");
    let pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    pt = pt.matrixTransform(svg.getScreenCTM().inverse());

    if (pointType === "p") {
      for (let handlePoint of handlePoints) {
        if (domPoints[handlePoint].visible) {
          domPoints[handlePoint].visible.style.transform =
            "translate(" + deltaX + "px, " + deltaY + "px)";
          domPoints[handlePoint].invisible.style.transform =
            "translate(" + deltaX + "px, " + deltaY + "px)";
          domPoints[handlePoint].visible.setAttribute("data-x", deltaX);
          domPoints[handlePoint].visible.setAttribute("data-y", deltaY);
        }
      }

      let newStart = [
        propsRef.current.s[0] + deltaX,
        propsRef.current.s[1] + deltaY,
      ];
      let newEnd = [
        propsRef.current.e[0] + deltaX,
        propsRef.current.e[1] + deltaY,
      ];
      let newMiddle = [
        propsRef.current.m[0] + deltaX,
        propsRef.current.m[1] + deltaY,
      ];
      let newH1 = propsRef.current.h1
        ? [propsRef.current.h1[0] + deltaX, propsRef.current.h1[1] + deltaY]
        : undefined;
      let newH2 = propsRef.current.h2
        ? [propsRef.current.h2[0] + deltaX, propsRef.current.h2[1] + deltaY]
        : undefined;

      let newAction = {
        ...propsRef.current,
        s: newStart,
        m: newMiddle,
        e: newEnd,
        h1: newH1,
        h2: newH2,
      };

      document
        .getElementById(props.uId + "_p")
        .setAttribute(
          "d",
          Helper.createPathV2(
            newAction,
            propsRef.current.links,
            playerCircleRadiusRef.current
          )
        );
      if (
        propsRef.current.subType === "pass" ||
        propsRef.current.subType === "shot"
      ) {
        propsRef.current.checkForPlayersAtThisPoint([newStart, newEnd]);
      } else {
        propsRef.current.checkForPlayersAtThisPoint([newStart]);
      }
    } else {
      let currentArrowPos = Helper.onlyPoints(propsRef.current);
      let arrowChanges = Helper.transformArrow(
        currentArrowPos,
        { point: pointType, pos: [pt.x, pt.y] },
        shape
      );
      let nextArrowPos = { ...currentArrowPos, ...arrowChanges };

      for (const [point, pos] of Object.entries(arrowChanges)) {
        let delta = [
          pos[0] - currentArrowPos[point][0],
          pos[1] - currentArrowPos[point][1],
        ];

        if (domPoints[point].visible) {
          domPoints[point].visible.style.transform =
            "translate(" + delta[0] + "px, " + delta[1] + "px)";
          domPoints[point].invisible.style.transform =
            "translate(" + delta[0] + "px, " + delta[1] + "px)";
        }
      }

      let newAction = {
        ...propsRef.current,
        ...arrowChanges,
      };

      document.getElementById(props.uId + "_p").setAttribute(
        "d",
        Helper.createPathV2(
          newAction,
          propsRef.current.links,

          playerCircleRadiusRef.current
        )
      );

      if (pointType === "s") {
        propsRef.current.checkForPlayersAtThisPoint([[pt.x, pt.y]]);
      }
      if (
        pointType === "e" &&
        ["pass", "shot"].includes(propsRef.current.subType)
      ) {
        propsRef.current.checkForPlayersAtThisPoint([[pt.x, pt.y]]);
      }
    }
  }

  useEffect(() => {
    if (props.editable && props.subType !== "hoop") {
      interact("#" + props.uId)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          if (propsRef.current.status !== "EDIT") {
            dispatch(tap({ id: props.id }));
          }
        })
        .on("tap", (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (propsRef.current.status !== "EDIT") {
            dispatch(tap({ id: props.id }));
          }
        })
        .on("doubletap", (event) => {
          if (
            propsRef.current.status !== "EDIT" &&
            propsRef.current.subType === "player"
          ) {
            dispatch(
              editObject({ actionId: props.id, changes: { status: "EDIT" } })
            );
          }
        })
        .draggable({
          modifiers: [
            interact.modifiers.restrict({
              restriction: "#editor_wrapper",
            }),
          ],
          onstart: () => {
            dispatch(interactionStart({ id: props.id, point: "s" }));
          },
          onend: updatePosition,
          onmove: dragMoveListener,
        });

      return () => interact("#" + props.uId).unset();
    }
  }, []); //eslint-disable-line

  let pixelPerFoot = props.pixelPerFoot;

  let factor = props.objectSizeFactor ?? 1;

  let { coneWidth, playerBoxWidth, playerCircleRadius, handOffWidth } =
    props.objectSizes;

  playerCircleRadius *= factor;
  coneWidth *= factor;
  handOffWidth *= factor;
  playerBoxWidth *= factor;

  const playerCircleRadiusRef = useCurrentValue(playerCircleRadius);

  let drawPaths = [];
  if (props.e && props.s) {
    drawPaths = Helper.createHandoffPath(
      props.links,
      props.s,
      props.pos,
      props.e,
      playerCircleRadius * 1
    );
  }

  const effectiveStatus = Helper.getEffectiveStatus(props);
  const strokeColor = getStrokeColor(effectiveStatus, props.color, theme);

  let adjustedEndPoints = Helper.adjustPointsV2(
    {
      s: props.s,
      m: props.pos,
      e: props.e,
      h1: undefined,
      h2: undefined,
      shape: 1,
    },
    props.links,
    playerCircleRadius
  );

  const showDragCircles = ["SELECTED", "HOVERING", "INTERACTING"].includes(
    effectiveStatus
  );

  return (
    <>
      <defs>
        <ArrowMarker
          id={"arrow_" + props.uId}
          width={8}
          length={7}
          color={strokeColor}
        />
      </defs>
      <g
        className={
          "draggable " + (props.linkedObjectHovering ? "linked_hovering" : "")
        }
        id={props.uId}
        onClick={(e) => {
          if (props.editable) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
        onMouseEnter={() => {
          if (
            props.editable &&
            props.status === "NOT_SELECTED" &&
            !props.interaction
          ) {
            dispatch(mouseEnter({ id: props.id }));
          }
        }}
        onMouseLeave={() => {
          if (
            props.editable &&
            props.status === "HOVERING" &&
            !props.interaction
          ) {
            dispatch(mouseLeave({ id: props.id }));
          }
        }}
        cx={props.pos[0]}
        cy={props.pos[1]}
      >
        {(props.status === "SELECTED" ||
          props.status === "INTERACTING" ||
          props.status === "HOVERING" ||
          props.status === "SELECTED_MULTIPLE") &&
          props.editable && (
            <>
              <rect
                style={{ fill: "rgb(240, 241, 243)", fillOpacity: "0.5" }}
                className={
                  "draggable " +
                  (props.status === "SELECTED_MULTIPLE"
                    ? "selected_multiple"
                    : "selected")
                }
                x={props.pos[0] - playerBoxWidth / 2}
                y={props.pos[1] - playerBoxWidth / 2}
                height={playerBoxWidth}
                width={playerBoxWidth}
                stroke="black"
                strokeWidth={1 / pixelPerFoot}
              />
            </>
          )}

        {drawPaths.length > 0 && (
          <>
            <path
              className="draggable"
              id={props.uId + "_p1"}
              strokeDasharray={"0.6  0.4"}
              strokeWidth={arrowStrokeWidth}
              fill="none"
              stroke={strokeColor}
              d={drawPaths[0]}
            />
            <path
              className="draggable"
              style={{ cursor: props.editable ? "move" : "inherit" }}
              id={props.uId + "_p1_o"}
              d={drawPaths[0]}
              strokeWidth={40 / pixelPerFoot}
              stroke={"transparent"}
              fill="none"
            />

            <path
              className="draggable"
              markerEnd={"url(#arrow_" + props.uId + ")"}
              id={props.uId + "_p2"}
              strokeDasharray={"0.6 0.4"}
              strokeWidth={arrowStrokeWidth}
              fill="none"
              stroke={strokeColor}
              d={drawPaths[1]}
            />

            <path
              className="draggable"
              style={{ cursor: props.editable ? "move" : "inherit" }}
              id={props.uId + "_p2_o"}
              d={drawPaths[1]}
              strokeWidth={40 / pixelPerFoot}
              stroke={"transparent"}
              fill="none"
            />

            {showDragCircles && (
              <>
                <DragCircle
                  x={adjustedEndPoints.s[0]}
                  y={adjustedEndPoints.s[1]}
                  pixelPerFoot={props.pixelPerFoot}
                />
                <DragCircle
                  x={adjustedEndPoints.e[0]}
                  y={adjustedEndPoints.e[1]}
                  pixelPerFoot={props.pixelPerFoot}
                />
              </>
            )}
          </>
        )}

        {props.editable && (
          <circle
            id={props.uId + "_hover_circle"}
            css={{
              fill: "none",
              ".target_hover &": { fill: "rgb(240, 241, 243)", fillOpacity: 1 },
              ".linked_hovering &": {
                fill: "rgb(240, 241, 243)",
                fillOpacity: 0.6,
              },
            }}
            cx={props.pos[0]}
            cy={props.pos[1]}
            r={playerCircleRadius}
            strokeWidth={1 / pixelPerFoot}
          />
        )}
        {/*      // DONT DELETE RECT BELOW IMPORTANT FOR REGISTERING CLICKS
         */}
        <rect
          className="draggable"
          x={props.pos[0] - playerCircleRadius}
          y={props.pos[1] - playerCircleRadius}
          width={playerCircleRadius * 2}
          height={playerCircleRadius * 2}
          fill="transparent"
        ></rect>

        {props.animatable && (
          <circle
            id={props.uId + "_ball"}
            cx={props.pos[0]}
            cy={props.pos[1]}
            r={(playerCircleRadius * 47) / 50}
            fill={"transparent"}
            stroke={props.color}
            opacity={props.team === "ball" ? 1 : 0}
            strokeWidth={playerCircleRadius * 2 * 0.07}
            shapeRendering="geometricPrecision"
          />
        )}

        <mask id={"mask_" + props.uId}>
          <rect
            fill="white"
            opacity="2"
            stroke="white"
            x={0}
            y={0}
            width={150}
            height={100}
          />
        </mask>

        <svg
          id={props.uId + "_handoff"}
          x={props.pos[0] - handOffWidth / 2}
          y={props.pos[1] - handOffWidth / 2}
          width={handOffWidth}
          height={handOffWidth}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 150 100"
        >
          <g mask={"url(#mask_" + props.uId + ")"}>
            <path
              strokeWidth="12"
              stroke={props.color}
              fill="none"
              d="M 0 50 L 150 50"
            />
            <path
              strokeWidth="12"
              stroke={props.color}
              fill="none"
              d="M 50 0 L 50 150"
            />
            <path
              strokeWidth="12"
              stroke={props.color}
              fill="none"
              d="M 100 0 L 100 150"
            />
          </g>
        </svg>
      </g>
    </>
  );
}

Handoff.propTypes = {
  id: PropTypes.string,
  uId: PropTypes.string,
  pos: PropTypes.array,
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  team: PropTypes.string,
  status: PropTypes.oneOf([
    "SELECTED",
    "SELECTED_MULTIPLE",
    "EDIT",
    "INTERACTING",
    "NOT_SELECTED",
  ]),
  subType: PropTypes.oneOf(["player", "handoff", "cone", "hoop"]),
  editable: PropTypes.bool,
  animatable: PropTypes.bool,
};
